import React, { useEffect, useMemo, useState } from "react";

import {
  X,
  Loader2,
  History,
  BookOpen,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import {
  ASSESSMENT_LEVEL_RL,
  buildRlGoalLevelOptions,
  buildRlLevelLookupMaps,
  resolveRlRationaleGuidance,
} from "./organizationLevelUtils";
import { useGrade, useRole } from "./RelationshipContexts";
import {
  ASSESSED_POSTURE_VALUES,
  GOAL_HORIZON_MAX,
  GOAL_HORIZON_MIN,
  RL_LEVELS,
  clampLevel,
  formatLevel,
  getRlBadgeClasses,
  getVendorGradeLabel,
  goalTargetMonthsToInputValue,
  normalizeGoalTargetMonths,
  normalizePosture,
  resolvePairAssessmentUserId,
  resolvePairContactId,
} from "./relationshipUtils";
import RlAssessmentHistoryPanel from "./rl-assessment-history-panel";
import { useToast } from "../../../Components/Toast";
import { useAppDispatch } from "../../../Store/Hooks";
import {
  fetchRelationshipAssessmentLevels,
  upsertRelationshipAssessment,
} from "../../../Store/Reducers/RelationshipSlice";
import { resolveInheritedPair } from "../../../Store/Reducers/TeamViewsSlice";

const POSTURE_OPTIONS = ASSESSED_POSTURE_VALUES;

const POSTURE_LABELS = {
  unknown: { letter: "U", label: "Unknown" },
  advocate: { letter: "A", label: "Advocate" },
  neutral: { letter: "N", label: "Neutral" },
  detractor: { letter: "D", label: "Detractor" },
};

function hasExistingAssessment(pair) {
  return pair?.rlLevel !== null && pair?.rlLevel !== undefined;
}

function formatPredecessorGoalSnippet(pair) {
  const goalRl = pair?.predecessorGoalTargetRl;
  if (goalRl === null || goalRl === undefined) return null;
  const months = pair?.predecessorGoalHorizonMonths;
  const horizon =
    months !== null && months !== undefined && months !== ""
      ? ` in ${months}m`
      : "";
  return `Goal RL ${goalRl}${horizon}`;
}

function buildInheritedBannerText(pair) {
  const priorOwner = pair?.precededByUserName || "the prior owner";
  const rl =
    pair?.predecessorRl ??
    (pair?.rlLevel !== null && pair?.rlLevel !== undefined
      ? clampLevel(pair.rlLevel)
      : null);
  const hasGoal =
    pair?.predecessorGoalTargetRl !== null &&
    pair?.predecessorGoalTargetRl !== undefined;
  const rlPart = rl !== null && rl !== undefined ? `RL ${rl}` : "RL ?";
  return `Inherited ${rlPart}${hasGoal ? " & goal" : ""} from ${priorOwner} — confirm to accept.`;
}

function buildPredecessorContextText(pair) {
  const priorRl =
    pair?.predecessorRl ??
    (pair?.rlLevel !== null && pair?.rlLevel !== undefined
      ? clampLevel(pair.rlLevel)
      : null);
  const parts = [priorRl != null ? `RL ${priorRl}` : "RL ?"];
  const goalSnippet = formatPredecessorGoalSnippet(pair);
  if (goalSnippet) parts.push(goalSnippet);
  const historyCount = Number(pair?.predecessorHistoryCount || 0);
  if (historyCount > 0) {
    parts.push(`${historyCount} history point${historyCount === 1 ? "" : "s"}`);
  }
  return `Prior record: ${parts.join(" · ")}. This remains a separate historical record.`;
}

function initialRlScore(pair) {
  if (!hasExistingAssessment(pair)) return null;
  return clampLevel(pair.rlLevel);
}

function initialPosture(pair) {
  if (!hasExistingAssessment(pair)) return "unknown";
  return normalizePosture(pair?.assessedPosture ?? "unknown");
}

function initialGoalTarget(pair) {
  const raw = pair?.goalTargetRl;
  if (raw === null || raw === undefined) return "";
  const current = pair?.rlLevel;
  if (
    current !== null &&
    current !== undefined &&
    clampLevel(raw) < clampLevel(current)
  )
    return "";
  return String(clampLevel(raw));
}

export function RlAssessmentModal({
  open,
  onClose,
  pair,
  stakeholder,
  contact,
  executiveId,
  onSubmitted,
}) {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { gradeMapping } = useGrade();
  const { considerMeddpiccRole } = useRole();
  const [rlScore, setRlScore] = useState(null);
  const [posture, setPosture] = useState("unknown");
  const [rationale, setRationale] = useState("");
  const [goalTarget, setGoalTarget] = useState("");
  const [goalHorizon, setGoalHorizon] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [localConfirmed, setLocalConfirmed] = useState(false);
  const [historyMode, setHistoryMode] = useState(null); // null | "self" | "prior"
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [rlLevelNames, setRlLevelNames] = useState({});
  const [rlLevelDescriptions, setRlLevelDescriptions] = useState({});
  const [rlLevelGuidance, setRlLevelGuidance] = useState({});
  const [showRationaleGuidance, setShowRationaleGuidance] = useState(false);

  const currentRl =
    rlScore === null || rlScore === undefined ? null : clampLevel(rlScore);

  const rationaleGuidance = useMemo(
    () => resolveRlRationaleGuidance(rlLevelGuidance, currentRl),
    [rlLevelGuidance, currentRl]
  );

  useEffect(() => {
    let cancelled = false;
    dispatch(fetchRelationshipAssessmentLevels(ASSESSMENT_LEVEL_RL))
      .unwrap()
      .then(levels => {
        if (cancelled) return;
        const { namesByLevel, descriptionsByLevel, guidanceByLevel } =
          buildRlLevelLookupMaps(levels);
        setRlLevelNames(namesByLevel);
        setRlLevelDescriptions(descriptionsByLevel);
        setRlLevelGuidance(guidanceByLevel);
      })
      .catch(err => {
        console.error("Failed to load RL level definitions:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const allGoalLevelOptions = useMemo(
    () => buildRlGoalLevelOptions(rlLevelNames),
    [rlLevelNames]
  );

  const goalLevelOptions = useMemo(
    () =>
      currentRl === null
        ? allGoalLevelOptions
        : allGoalLevelOptions.filter(opt => opt.value >= currentRl),
    [allGoalLevelOptions, currentRl]
  );

  useEffect(() => {
    if (!open || !pair) return;
    setRlScore(initialRlScore(pair));
    setPosture(initialPosture(pair));
    setRationale(pair.rationale || "");
    setGoalTarget(initialGoalTarget(pair));
    setGoalHorizon(goalTargetMonthsToInputValue(pair.goalTargetHorizon));
    setHistoryMode(null);
    setLocalConfirmed(false);
    if (hasExistingAssessment(pair)) {
      setShowRationaleGuidance(true);
    }
  }, [
    open,
    pair?.id,
    pair?.rlLevel,
    pair?.assessedPosture,
    pair?.rationale,
    pair?.goalTargetRl,
    pair?.goalTargetHorizon,
    pair?.pendingConfirm,
  ]);

  useEffect(() => {
    if (!goalTarget || currentRl === null) return;
    if (Number(goalTarget) < currentRl) setGoalTarget("");
  }, [currentRl, goalTarget]);

  if (!open || !pair) return null;

  const reviewPending = Boolean(pair.pendingConfirm) && !localConfirmed;
  const fieldsLocked = reviewPending;
  const priorOwnerName = pair.precededByUserName || "the prior owner";
  const historyUserId =
    historyMode === "prior" && pair.precededByUserId
      ? String(pair.precededByUserId)
      : resolvePairAssessmentUserId(pair, stakeholder, executiveId);
  const historyStakeholderName =
    historyMode === "prior" ? priorOwnerName : stakeholder?.name;

  const handleConfirmInherited = async () => {
    setConfirming(true);
    try {
      await dispatch(
        resolveInheritedPair({
          accountId: pair.accountId,
          userId: resolvePairAssessmentUserId(pair, stakeholder, executiveId),
          contactId: resolvePairContactId(pair, contact),
          action: "confirm",
        })
      ).unwrap();
      setLocalConfirmed(true);
      toast.success("Inherited coverage confirmed.");
      await onSubmitted?.({ pairId: pair.id });
    } catch (err) {
      toast.error("Could not confirm inherited coverage.");
    } finally {
      setConfirming(false);
    }
  };

  const handleRejectInherited = async () => {
    setRejecting(true);
    try {
      const result = await dispatch(
        resolveInheritedPair({
          accountId: pair.accountId,
          userId: resolvePairAssessmentUserId(pair, stakeholder, executiveId),
          contactId: resolvePairContactId(pair, contact),
          action: "reject",
        })
      ).unwrap();
      const restoredTo = result?.restored_to;
      if (restoredTo === "active") {
        toast.success(
          "Transfer cancelled. Coverage restored to the prior owner."
        );
      } else {
        toast.success(
          "Transfer cancelled. Coverage returned to the Departed Row."
        );
      }
      await onSubmitted?.({ pairId: pair.id, rejectedInherited: true });
      onClose?.();
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err?.message || "Could not cancel inherited coverage.";
      toast.error(message);
    } finally {
      setRejecting(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (fieldsLocked) {
      toast.warning("Confirm inherited coverage before marking a new RL.");
      return;
    }
    if (posture === null || posture === "") {
      toast.warning("Please select an assessed posture.");
      return;
    }
    if (rlScore === null || rlScore === undefined) {
      toast.warning("Please select a relationship level.");
      return;
    }
    const goalMonths = normalizeGoalTargetMonths(goalHorizon);
    if (
      goalTarget &&
      Number(goalTarget) >= currentRl &&
      goalHorizon.trim() &&
      goalMonths === null
    ) {
      toast.warning(
        `Target months must be between ${GOAL_HORIZON_MIN} and ${GOAL_HORIZON_MAX}.`
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        rl_score: Number(rlScore),
        assessed_posture: normalizePosture(posture),
        rationale: rationale.trim() || rationaleGuidance,
        executive_id: executiveId || undefined,
        is_primary_assessment: true,
        account_id: pair.accountId,
        user_id: resolvePairAssessmentUserId(pair, stakeholder, executiveId),
        contact_id: resolvePairContactId(pair, contact),
      };
      if (goalTarget && Number(goalTarget) >= currentRl) {
        payload.target_rl_score = Number(goalTarget);
        if (goalMonths !== null) {
          payload.goal_target_horizon = goalMonths;
        }
      }

      await dispatch(
        upsertRelationshipAssessment({
          accountId: pair.accountId,
          pairId: pair.id,
          payload,
        })
      ).unwrap();
      setHistoryRefreshKey(key => key + 1);
      await onSubmitted?.({
        pairId: pair.id,
        rlScore: Number(rlScore),
        assessedPosture: posture,
        rationale: rationale.trim() || rationaleGuidance,
        goalTargetRl:
          goalTarget && Number(goalTarget) >= currentRl
            ? Number(goalTarget)
            : null,
        goalTargetHorizon: goalMonths,
      });
      onClose();
    } catch (err) {
      console.error("RL assessment submit failed:", err);
      toast.error(
        err?.response?.data?.error || "Failed to save relationship assessment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="rl-assessment-modal-overlay fixed inset-0 flex items-center justify-center bg-black/50 p-4">
      <article className="rl-prototype-skin flex w-full max-w-xl min-h-[calc(100vh-8rem)] max-h-[calc(100vh-8rem)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
        {historyMode ? (
          <RlAssessmentHistoryPanel
            accountId={pair.accountId}
            userId={historyUserId}
            contactId={resolvePairContactId(pair, contact)}
            pair={pair}
            stakeholder={stakeholder}
            contact={contact}
            executiveId={executiveId}
            stakeholderName={historyStakeholderName}
            contactName={contact?.name}
            refreshKey={historyRefreshKey}
            onBack={() => setHistoryMode(null)}
          />
        ) : (
          <>
            <header className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {hasExistingAssessment(pair)
                    ? "Update Assessment"
                    : "Assess Relationship"}
                </h2>
                <p className="mt-0.5 text-sm text-gray-500">
                  {stakeholder?.name}
                  {getVendorGradeLabel(stakeholder?.ladderLevel, gradeMapping)
                    ? ` (${getVendorGradeLabel(
                        stakeholder?.ladderLevel,
                        gradeMapping
                      )})`
                    : ""}{" "}
                  ↔ {contact?.name}
                  {considerMeddpiccRole && contact?.meddpiccRole
                    ? ` (${contact.meddpiccRole})`
                    : ""}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {!reviewPending ? (
                  <button
                    type="button"
                    onClick={() => setHistoryMode("self")}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    <History className="h-3.5 w-3.5" />
                    RL History
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </header>

            <form
              onSubmit={handleSubmit}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
                {reviewPending ? (
                  <>
                    <div
                      id="rl-pending-confirm-banner"
                      className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-900"
                    >
                      <p className="min-w-0 flex-1 leading-snug">
                        {buildInheritedBannerText(pair)}
                      </p>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          disabled={confirming || rejecting}
                          className="rounded-full border border-purple-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-50 disabled:opacity-60"
                          onClick={handleRejectInherited}
                        >
                          {rejecting ? "Cancelling…" : "Cancel"}
                        </button>
                        <button
                          type="button"
                          disabled={confirming || rejecting}
                          className="rounded-full bg-purple-600 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-purple-700 disabled:opacity-60"
                          onClick={handleConfirmInherited}
                        >
                          {confirming ? "Confirming…" : "Confirm"}
                        </button>
                      </div>
                    </div>

                    {(pair.precededByUserId || pair.precededByUserName) && (
                      <div
                        id="rl-predecessor-context"
                        className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700"
                      >
                        <div className="min-w-0 flex-1">
                          <strong className="text-gray-900">
                            Inherited context from {priorOwnerName}
                          </strong>
                          <div className="mt-0.5 text-gray-600">
                            {buildPredecessorContextText(pair)}
                          </div>
                        </div>
                        {pair.precededByUserId ? (
                          <button
                            type="button"
                            className="shrink-0 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50"
                            onClick={() => setHistoryMode("prior")}
                          >
                            View prior history
                          </button>
                        ) : null}
                      </div>
                    )}
                  </>
                ) : null}

                <fieldset
                  disabled={fieldsLocked}
                  className={
                    fieldsLocked
                      ? "space-y-4 opacity-55 pointer-events-none"
                      : "space-y-4"
                  }
                >
                  <section>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Assessed posture
                    </label>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {POSTURE_OPTIONS.map(opt => {
                        const normalized = normalizePosture(opt);
                        const info =
                          POSTURE_LABELS[normalized] || POSTURE_LABELS.unknown;
                        const isSelected =
                          posture !== null &&
                          normalizePosture(posture) === normalized;
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setPosture(normalized)}
                            className={`rounded-lg border px-3 py-2 text-left text-sm font-semibold transition ${
                              isSelected
                                ? "border-slate-900 bg-slate-900 text-white"
                                : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                            }`}
                          >
                            {info.letter} · {info.label}
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Relationship level (RL 0–RL 5)
                    </label>
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                      {RL_LEVELS.map(level => (
                        <button
                          key={level}
                          type="button"
                          onClick={() => {
                            setRlScore(level);
                            setShowRationaleGuidance(true);
                          }}
                          className={`rounded-lg border px-2 py-2 text-left text-xs font-bold transition ${
                            rlScore !== null && Number(rlScore) === level
                              ? `${getRlBadgeClasses(level)} ring-2 ring-purple-500`
                              : "border-gray-200 bg-white text-gray-600"
                          }`}
                        >
                          <div>{formatLevel("RL", level)}</div>
                          <div className="mt-0.5 text-[9px] font-medium opacity-80">
                            {rlLevelNames[level] || ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  </section>

                  <section className="zappy">
                    <span className="zappy-icon" aria-hidden>
                      ⚡
                    </span>
                    <span>
                      <b>Zappy:</b>{" "}
                      {rlLevelDescriptions[currentRl] ||
                        "Select a relationship level to see Zappy's insight."}
                    </span>
                  </section>

                  <section>
                    {currentRl !== null && rationaleGuidance ? (
                      <div className="mb-3">
                        <button
                          type="button"
                          onClick={() =>
                            setShowRationaleGuidance(prev => !prev)
                          }
                          className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                        >
                          <BookOpen size={13} aria-hidden />
                          Rationale guidance
                          {showRationaleGuidance ? (
                            <ChevronUp size={12} aria-hidden />
                          ) : (
                            <ChevronDown size={12} aria-hidden />
                          )}
                        </button>
                        {showRationaleGuidance ? (
                          <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm leading-relaxed text-indigo-800">
                            {rationaleGuidance}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <label className="mb-1 block text-sm font-semibold text-gray-700">
                      Rationale (optional)
                    </label>
                    <textarea
                      value={rationale}
                      onChange={e => setRationale(e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                      placeholder={
                        currentRl !== null
                          ? rationaleGuidance
                          : "Select a relationship level to see rationale guidance…"
                      }
                    />
                  </section>

                  <section className="goal-sec">
                    <div className="goal-sec-hdr">
                      🎯 Set a Goal for this Relationship
                      <span
                        style={{
                          fontWeight: 400,
                          color: "var(--rl-2-text, #ca8a04)",
                        }}
                      >
                        {" "}
                        (optional)
                      </span>
                    </div>
                    <div className="goal-fields">
                      <div>
                        <label
                          className="lbl"
                          style={{ color: "var(--rl-3-text, #ca8a04)" }}
                        >
                          Target RL Level
                        </label>
                        <select
                          className="form-sel w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm"
                          value={goalTarget}
                          onChange={e => setGoalTarget(e.target.value)}
                          disabled={
                            fieldsLocked ||
                            currentRl === null ||
                            goalLevelOptions.length === 0
                          }
                        >
                          <option value="">No goal</option>
                          {goalLevelOptions.map(opt => (
                            <option key={opt.value} value={String(opt.value)}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {goalLevelOptions.length === 0 ? (
                          <p className="mt-1 text-[10px] text-amber-700">
                            Already at RL5 — no higher target available.
                          </p>
                        ) : null}
                      </div>
                      <div>
                        <label
                          className="lbl"
                          style={{ color: "var(--rl-3-text, #ca8a04)" }}
                        >
                          Target Date (months)
                        </label>
                        <input
                          type="number"
                          min={GOAL_HORIZON_MIN}
                          max={GOAL_HORIZON_MAX}
                          step={1}
                          inputMode="numeric"
                          className="form-sel w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm"
                          value={goalHorizon}
                          onChange={e => {
                            const next = e.target.value;
                            if (next === "") {
                              setGoalHorizon("");
                              return;
                            }
                            const parsed = Number(next);
                            if (!Number.isFinite(parsed)) return;
                            const clamped = Math.min(
                              GOAL_HORIZON_MAX,
                              Math.max(GOAL_HORIZON_MIN, Math.floor(parsed))
                            );
                            setGoalHorizon(String(clamped));
                          }}
                          disabled={fieldsLocked || !goalTarget}
                          placeholder={`Months (${GOAL_HORIZON_MIN}–${GOAL_HORIZON_MAX})`}
                          aria-label={`Target months between ${GOAL_HORIZON_MIN} and ${GOAL_HORIZON_MAX}`}
                        />
                      </div>
                    </div>
                  </section>
                </fieldset>
              </div>

              <footer className="flex shrink-0 justify-end gap-2 border-t border-gray-200 bg-white px-5 py-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || fieldsLocked}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-60"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save Assessment
                </button>
              </footer>
            </form>
          </>
        )}
      </article>
    </section>
  );
}

export default RlAssessmentModal;
